import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, FileText, Send, Loader2, MapPin, GraduationCap, Phone, Sparkles, Home } from 'lucide-react'; 
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const PostJob: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth(); 
  const [formData, setFormData] = useState({ 
    subject: '', 
    description: '', 
    location: '', 
    gradeLevel: '',
    parentPhone: '' 
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.phoneNumber) {
      setFormData(prev => ({ ...prev, parentPhone: user.phoneNumber ?? ''}));
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/jobs/post', formData);
      navigate('/student/my-jobs');
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || t('server_connection_failed', "Server connection failed");
      alert(`${t('failed_to_post_job', 'Failed to post job:')} ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col overflow-hidden relative">
      
      <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
        <div className="w-full max-w-2xl mx-auto p-4 md:p-10 pb-32">
          
          <div className="bg-slate-900 md:border-2 border-slate-800 rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl">
            
            <div className="bg-gradient-to-br from-indigo-700 to-slate-900 p-6 md:p-10 text-center">
                <div className="inline-flex p-3 bg-indigo-500/20 rounded-2xl mb-4 text-indigo-400">
                    <Sparkles size={24} />
                </div>
                <h2 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter text-white">{t('request_a_tutor', 'Request a Tutor')}</h2>
                <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-2 italic leading-relaxed">
                  {t('rates_fixed_by_admin', 'Rates are fixed by admin to ensure quality and fairness')}
                </p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 md:p-10 space-y-5 md:space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{t('subject', 'Subject')}</label>
                  <div className="relative">
                    <BookOpen className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input required className="w-full bg-slate-950 border-2 border-slate-800 rounded-xl md:rounded-2xl py-3.5 pl-11 pr-4 focus:border-indigo-500 outline-none transition-all font-bold text-sm text-white"
                      placeholder={t('eg_physics', 'e.g. Physics')}
                      value={formData.subject}
                      onChange={e => setFormData({...formData, subject: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{t('grade_level', 'Grade Level')}</label>
                  <div className="relative">
                    <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input required className="w-full bg-slate-950 border-2 border-slate-800 rounded-xl md:rounded-2xl py-3.5 pl-11 pr-4 focus:border-indigo-500 outline-none transition-all font-bold text-sm text-white"
                      placeholder={t('eg_grade_12', 'e.g. Grade 12')}
                      value={formData.gradeLevel}
                      onChange={e => setFormData({...formData, gradeLevel: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{t('school_name_location', 'School Name / Location')}</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input required className="w-full bg-slate-950 border-2 border-slate-800 rounded-xl md:rounded-2xl py-3.5 pl-11 pr-4 focus:border-indigo-500 outline-none transition-all font-bold text-sm text-white"
                      placeholder={t('eg_school_location', 'e.g. School Location')}
                      value={formData.location}
                      onChange={e => setFormData({...formData, location: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{t('contact_parent_phone', "Contact/Parent's Phone")}</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input required className="w-full bg-slate-950 border-2 border-slate-800 rounded-xl md:rounded-2xl py-3.5 pl-11 pr-4 focus:border-indigo-500 outline-none transition-all font-bold text-sm text-white"
                      placeholder="0911..."
                      value={formData.parentPhone}
                      onChange={e => setFormData({...formData, parentPhone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 block ml-1">{t('detailed_requirements', 'Detailed Requirements')}</label>
                <div className="relative">
                    <FileText className="absolute left-4 top-4 text-slate-600" size={16} />
                    <textarea required rows={4} className="w-full bg-slate-950 border-2 border-slate-800 rounded-xl md:rounded-2xl py-3.5 pl-11 pr-4 focus:border-indigo-500 outline-none transition-all font-medium text-sm leading-relaxed text-white resize-none"
                      placeholder={t('detailed_req_placeholder', 'Describe what you need help with (specific chapters, exam prep, etc.)')}
                      value={formData.description}
                      onChange={e => setFormData({...formData, description: e.target.value})}
                    />
                </div>
              </div>

              <button 
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 py-4 mt-4 rounded-xl md:rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50 text-xs text-white"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {t('post_tutor_request', 'Post Tutor Request')}
              </button>
            </form>
          </div>
        </div>
      </div>

      <button onClick={() => navigate('/student/home')} className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default PostJob;